import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetWHDevicesQuery } from "store/api/warehouseManagerApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedDeviceIDs } from "store/tempDataSlise"
import { warehouseManagerDeviceStatusOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"

import { convertWHManagerDevicessToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AddDeviceModal } from "modals/AddDeviceModal"
import { ChangeDeviceStatusModalForVendor, type VendorDeviceStatus } from "modals/ChangeDeviceStatusModalForVendor"
import { DamageDeviceModalForWarehouse } from "modals/DamageDeviceModalForWarehouse"
import { MissingModalWarehouse } from "modals/MissingModalWarehouse"
import { ReturnFromMissingModalWarehouse } from "modals/ReturnFromMissingModalWarehouse"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { StatusOfPhysical } from "components/table/columnComponents/StatusOfPhysical"
import TableWrapper from "components/table/TableWrapper"
import { WidgetDevicesStatus } from "components/widgets/WidgetDevicesStatus"

import styles from "./index.module.scss"

// TABLE HEADER
const tableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    name: "checkbox",
    type: "checkbox",
    size: "mini",
    cellComponent: "checkbox",
  },
  {
    title: "S/N",
    type: "sort-search",
    size: "large",
    name: "sn",
  },
  {
    title: "Date of Input",
    type: "sort-date",
    size: "large",
    name: "input_date",
  },
  {
    title: "Status",
    type: "select",
    size: "large",
    name: "phisical_status",
    options: warehouseManagerDeviceStatusOptions,
    cellComponent: StatusOfPhysical,
  },
]

export const Devices = () => {
  const [triggerGetWHDevices, { data, isFetching }] = useLazyGetWHDevicesQuery()
  const dispatch = useAppDispatch()
  const { urlQueryParams } = useUrlQueryParams()
  const selectedDeviceIDs = useAppSelector(state => state.tempData.selectedDeviceIDs)
  const [selectedDeviceSNsByID, setSelectedDeviceSNsByID] = useState<Record<number, string>>({})

  const isHeaderChecked = useMemo(() => {
    if (!data?.data?.length) return false
    return data.data.every(device => selectedDeviceIDs?.includes(device.id))
  }, [data, selectedDeviceIDs])
  const [isFirstRender, setFirstRender] = useState(true)
  const [showMissingModal, setShowMissingModal] = useState(false)
  const [showReturnFromMissingModal, setShowReturnFromMissingModal] = useState(false)
  const [showDamageModal, setShowDamageModal] = useState(false)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false)
  const [statusToChange, setStatusToChange] = useState<VendorDeviceStatus>()
  const [updateIndex, setUpdateIndex] = useState(0)

  const toggleAllItems = () => {
    const currentPageDeviceIDs = data?.data?.map(item => item.id) || []

    if (isHeaderChecked) {
      dispatch(setSelectedDeviceIDs(selectedDeviceIDs?.filter(id => !currentPageDeviceIDs.includes(id)) || []))
    } else {
      const newIDs = currentPageDeviceIDs.filter(id => !selectedDeviceIDs?.includes(id))
      dispatch(setSelectedDeviceIDs([...(selectedDeviceIDs || []), ...newIDs]))
      setSelectedDeviceSNsByID(prev => ({
        ...prev,
        ...Object.fromEntries(data?.data?.map(item => [item.id, item.sn]) || []),
      }))
    }
  }

  const onSelectItem = (id?: number) => {
    if (selectedDeviceIDs?.some(item => item === id)) {
      dispatch(setSelectedDeviceIDs(selectedDeviceIDs?.filter(item => item !== id)))
    } else {
      dispatch(setSelectedDeviceIDs([...selectedDeviceIDs, ...[id]] as number[]))
      const selectedDevice = data?.data?.find(device => device.id === id)
      if (selectedDevice) {
        setSelectedDeviceSNsByID(prev => ({ ...prev, [selectedDevice.id]: selectedDevice.sn }))
      }
    }
  }

  const onDamagedClick = () => {
    setShowDamageModal(true)
  }

  const hasAnyMissingDevice = () => {
    if (!selectedDeviceIDs?.length || !data?.data) return false
    return data.data
      .filter(device => selectedDeviceIDs.includes(device.id))
      .some(device => device.phisical_status === "missing")
  }

  const allDevicesAreMissing = () => {
    if (!selectedDeviceIDs?.length || !data?.data) return false
    return data.data
      .filter(device => selectedDeviceIDs.includes(device.id))
      .every(device => device.phisical_status === "missing")
  }

  const onMissingClick = () => {
    if (hasAnyMissingDevice() && !allDevicesAreMissing()) {
      return
    }

    if (hasAnyMissingDevice()) {
      setShowReturnFromMissingModal(true)
    } else {
      setShowMissingModal(true)
    }
  }

  const checkButtonsAvailability = (statuses: string[]) => {
    if (!selectedDeviceIDs?.length || !data?.data) return false

    return data.data
      .filter(device => selectedDeviceIDs.includes(device.id))
      .every(device => statuses.includes(device.phisical_status))
  }

  const onChangeStatusClick = (status: VendorDeviceStatus) => {
    setStatusToChange(status)
  }

  const fetchData = async () => {
    sendRequest(urlQueryParams)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetWHDevices(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    fetchData()
  }, [urlQueryParams, isFirstRender])

  const handleSuccess = () => {
    setShowMissingModal(false)
    setShowReturnFromMissingModal(false)
    setShowDamageModal(false)
    setShowAddDeviceModal(false)
    setStatusToChange(undefined)
    setUpdateIndex(prev => prev + 1)
    dispatch(setSelectedDeviceIDs([]))
    setSelectedDeviceSNsByID({})
    fetchData()
  }

  return (
    <>
      <WidgetDevicesStatus updateIndex={updateIndex} />
      <ContentWrapper
        title="Devices"
        renderHeaderAccessoryView={() => (
          <ButtonNew onClick={() => setShowAddDeviceModal(true)} color="blue">
            Add New
          </ButtonNew>
        )}
      >
        <nav className={styles.tableNavWrapper}>
          <div className={styles.actionButtonsWrapper}>
            <button
              onClick={onMissingClick}
              disabled={!selectedDeviceIDs?.length || (hasAnyMissingDevice() && !allDevicesAreMissing())}
              className={classNames(styles.actionButton, styles.missing)}
            >
              {hasAnyMissingDevice() ? "Return from Missing" : "Mark as Missing"}
            </button>
            <button
              onClick={onDamagedClick}
              disabled={!selectedDeviceIDs?.length}
              className={classNames(styles.actionButton, styles.damaged)}
            >
              Mark as Damaged
            </button>
            <button
              onClick={() => onChangeStatusClick("repackaged")}
              disabled={!checkButtonsAvailability(["stocked"])}
              className={classNames(styles.actionButton, styles.packaged)}
            >
              Mark as Repackaged
            </button>
            <button
              onClick={() => onChangeStatusClick("returned")}
              disabled={!checkButtonsAvailability(["stocked"])}
              className={classNames(styles.actionButton, styles.returned)}
            >
              Mark as Returned
            </button>
            <button
              onClick={() => onChangeStatusClick("refurbished")}
              disabled={!checkButtonsAvailability(["stocked"])}
              className={classNames(styles.actionButton, styles.refurbished)}
            >
              Mark as Refurbished
            </button>
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          data={convertWHManagerDevicessToTableRows(data?.data)}
          isLoading={isFetching}
          onCheckboxChange={onSelectItem}
          onHeaderCheckboxChange={toggleAllItems}
          currentItemIDs={selectedDeviceIDs}
          isHeaderChecked={isHeaderChecked}
          additionalTags={
            selectedDeviceIDs?.length
              ? [
                  {
                    label: `Selected Device${selectedDeviceIDs.length > 1 ? "s" : ""} S/N`,
                    values: selectedDeviceIDs
                      .map(id => selectedDeviceSNsByID[id] || data?.data?.find(device => device.id === id)?.sn)
                      .filter((sn): sn is string => !!sn),
                  },
                ]
              : []
          }
          clearAdditionalTags={() => {
            dispatch(setSelectedDeviceIDs([]))
            setSelectedDeviceSNsByID({})
          }}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
      {showMissingModal && (
        <MissingModalWarehouse onCancel={() => setShowMissingModal(false)} onSuccess={handleSuccess} />
      )}
      {showReturnFromMissingModal && (
        <ReturnFromMissingModalWarehouse
          onCancel={() => setShowReturnFromMissingModal(false)}
          onSuccess={handleSuccess}
        />
      )}
      {showDamageModal && (
        <DamageDeviceModalForWarehouse onCancel={() => setShowDamageModal(false)} onSuccess={handleSuccess} />
      )}
      {statusToChange && (
        <ChangeDeviceStatusModalForVendor
          status={statusToChange}
          onCancel={() => setStatusToChange(undefined)}
          onSuccess={handleSuccess}
        />
      )}
      {showAddDeviceModal && <AddDeviceModal onCancel={() => setShowAddDeviceModal(false)} onSuccess={handleSuccess} />}
    </>
  )
}
